import { IBenefit, ICoverage } from '../container/account/benefits/components/interfaces/ICommon';
import {
  ICartItem,
  ICartSummary,
  ICustomLineItem,
  IProductLineItem,
} from '../container/cart/ICart';
import { IUserProfile } from '../providers/user-provider';
import { ILineItem } from '../services/helper/cart/cartInterface';
import { ProductTypes } from '../services/helper/cart/constants';
import { formatPrice } from '../services/helper/cart/reshapeCartData';

export const calculateTotalSavings = (item: ILineItem, customLineItems: ICustomLineItem[]) => {
  if (!item?.totalPrice?.centAmount || !item?.price?.value?.centAmount || !item?.quantity) {
    return null;
  }
  const matchingLineItems = customLineItems?.filter(
    customLineItem => customLineItem.custom.fields.appliedOnLineItem === item.id,
  );

  const totalSavings = matchingLineItems?.reduce((sum, customLineItem) => {
    return sum + customLineItem.totalPrice.centAmount;
  }, 0);

  return totalSavings;
};

export const calculateCartDiscount = (cart: ICartSummary) => {
  let totalDiscount = 0;
  cart?.customLineItems?.forEach((item: ICustomLineItem) => {
    totalDiscount += item.totalPrice.centAmount;
  });
  return formatPrice(parseFloat((Math.abs(totalDiscount) / 100).toString()));
};

export const calculateSavingsPlanDiscount = (discount: string, item: ICartItem) => {
  if (item.discount !== undefined && !isNaN(parseFloat(item.discount))) {
    const ItemDiscount = formatPrice(parseFloat(item.discount));
    discount =
      discount !== undefined
        ? (parseFloat(discount) + parseFloat(ItemDiscount)).toFixed(2)
        : ItemDiscount;

    return discount;
  } else if (discount !== undefined) {
    return discount;
  } else {
    return '0.00';
  }
};
export const calculateCartBenefits = (discount: string, item: ICartItem) => {
  if (item.benefits !== undefined && !isNaN(parseFloat(item.benefits))) {
    const ItemDiscount = formatPrice(parseFloat(item.benefits));
    discount =
      discount !== undefined
        ? (parseFloat(discount) + parseFloat(ItemDiscount)).toFixed(2)
        : ItemDiscount;

    return discount;
  } else if (discount !== undefined) {
    return discount;
  } else {
    return '0.00';
  }
};
export const calculateCartPromotions = (cart: ICartSummary) => {
  const totals: { [key: string]: number } = {};

  cart?.customLineItems?.forEach((item: ICustomLineItem) => {
    const deductionCategory = item.custom.fields.deductionCategory;
    const centAmount = item.money.centAmount;
    if (!totals[deductionCategory]) {
      totals[deductionCategory] = 0;
    }
    totals[deductionCategory] += Math.abs(centAmount);
  });

  return totals;
};

const isNumber = (input: any) => typeof (input * 1) === 'number' && !isNaN(input) && input !== '';

export const calculateChildrenTotal = (childrenLineItems: IProductLineItem[]) => {
  const price =
    childrenLineItems.reduce((total, curr) => {
      let lineItemTotal = 0;
      (curr?.lineItems || []).forEach(item => {
        if (isNumber(item.price)) {
          lineItemTotal += parseFloat(item.price);
        }
      });
      total += parseFloat(lineItemTotal.toString());
      return total;
    }, 0) || 0;

  return price;
};

export const calculateCartTotal = (cart: ICartSummary) => {
  return formatPrice(parseFloat(cart.subTotal) - parseFloat(cart.savings));
};
export const calculateCartSubTotal = (cartItems: ICartItem[]) => {
  let subtotal = 0;
  for (const item of cartItems) {
    let itemPrice = 0;
    if (item.type === ProductTypes.FRAME) {
      item?.childrenLineItems?.forEach(child => {
        child.lineItems.forEach(lineItem => {
          itemPrice += parseFloat(lineItem.price.replace(/[^0-9.]/g, ''));
        });
      });
    } else {
      itemPrice = item.orgPrice
        ? parseFloat(item.orgPrice.replace(/[^0-9.]/g, ''))
        : item.price
          ? parseFloat(item.price.replace(/[^0-9.]/g, ''))
          : 0;
    }
    const itemQuantity = item.qty ?? 1;
    subtotal += itemPrice * itemQuantity;
  }

  return formatPrice(subtotal);
};

/** Original price is used if there's any discounted price available */
export const getOriginalPrice = (cartItem: ICartItem, lineItem: ILineItem) => {
  return cartItem.savings !== '0.00'
    ? formatPrice(lineItem?.price?.value?.centAmount / 100)
    : undefined;
};

export const getFinalPrice = (lineItem: ILineItem, customLineItems: ICustomLineItem[]) => {
  const matchingLineItems = (customLineItems || []).filter(
    customLineItem => customLineItem.custom.fields.appliedOnLineItem === lineItem.id,
  );
  const totalDeductions = matchingLineItems.reduce((sum, customLineItem) => {
    return sum + customLineItem.totalPrice.centAmount;
  }, 0);

  const finalPrice =
    (lineItem?.price?.value?.centAmount ?? 0) + totalDeductions / (lineItem.quantity ?? 1);

  return formatPrice(finalPrice / 100);
};

export const checkBenefitAmount = (
  userProfile: IUserProfile | null,
  newSelectedBenefit: string | undefined,
  resBenefitAmount: number,
): boolean => {
  if (!newSelectedBenefit) return false;

  const matchingBenefit = userProfile?.benefits.find(
    (benefit: IBenefit) => benefit.id === newSelectedBenefit,
  );

  if (matchingBenefit && isNumber(resBenefitAmount)) {
    const availableAmount = matchingBenefit.coverage.reduce((total: number, item: ICoverage) => {
      return total + item.amountAvailable;
    }, 0);

    return resBenefitAmount < availableAmount;
  }

  return false;
};
